"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { GdpForm, type Row } from "@/components/tools/econ-macro/gdp-form";
import { GdpResults } from "@/components/tools/econ-macro/gdp-results";
import { MACRO_CHART_STRINGS } from "@/components/tools/econ-macro/line-figure";
import { CurrencyField } from "@/components/tools/finance/shared";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  analysePeriods,
  type Known,
  type PeriodInput,
  type PeriodResult,
  validPeriod,
} from "@/lib/tool-kit/econ-macro/gdp";

/** English strings. A translated content file overrides these through `ui`. */
export const REAL_GDP_CALCULATOR_STRINGS = {
  ...MACRO_CHART_STRINGS,
  knownLabel: "You have",
  knownNominalDeflator: "Nominal GDP and the GDP deflator → find real GDP",
  knownRealDeflator: "Real GDP and the GDP deflator → find nominal GDP",
  knownNominalReal: "Nominal GDP and real GDP → find the GDP deflator",
  inputsHeading: "Your periods",
  inputsHint:
    "Enter one or more periods in time order; with two or more you also get growth rates, each compared with the period before it. The deflator is 100 in the base year.",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  scaleLabel: "GDP is entered in",
  scaleUnits: "Single units (as entered)",
  scaleThousand: "Thousands",
  scaleMillion: "Millions",
  scaleBillion: "Billions",
  scaleTrillion: "Trillions",
  suffixThousand: "thousand",
  suffixMillion: "million",
  suffixBillion: "billion",
  suffixTrillion: "trillion",
  amountWithScale: "{amount} {scale}",
  populationScaleLabel: "Population is entered in",
  populationPeople: "People",
  populationThousand: "Thousands",
  populationMillion: "Millions",
  periodHeading: "Period {n}",
  periodName: "Period name",
  periodDefault: "Year {n}",
  nominalField: "Nominal GDP",
  realField: "Real GDP",
  deflatorField: "GDP deflator (base year = 100)",
  populationField: "Population (optional)",
  addPeriod: "Add a period",
  removePeriod: "Remove period {n}",
  invalidPeriod:
    "Period {n}: enter positive numbers for both values. Nothing here can be zero or negative.",
  resultsHeading: "Results",
  latestHeading: "Latest period: {label}",
  realGrowthCard: "Real GDP growth",
  inflationCard: "Inflation (GDP deflator)",
  nominalGrowthCard: "Nominal GDP growth",
  realPerCapitaCard: "Real GDP per person",
  tableHeading: "Nominal GDP, real GDP and the deflator",
  growthHeading: "Growth from the previous period",
  periodColumn: "Period",
  nominalColumn: "Nominal GDP",
  realColumn: "Real GDP",
  deflatorColumn: "GDP deflator",
  perCapitaColumn: "Real GDP per person",
  nominalGrowthColumn: "Nominal growth",
  realGrowthColumn: "Real growth",
  inflationColumn: "Inflation",
  perCapitaGrowthColumn: "Real per-person growth",
  notAvailable: "—",
  workingHeading: "Working for {label}",
  workReal:
    "Real GDP = nominal GDP ÷ (deflator ÷ 100) = {nominal} ÷ ({deflator} ÷ 100) = {real}",
  workNominal:
    "Nominal GDP = real GDP × (deflator ÷ 100) = {real} × ({deflator} ÷ 100) = {nominal}",
  workDeflator:
    "GDP deflator = (nominal GDP ÷ real GDP) × 100 = ({nominal} ÷ {real}) × 100 = {deflator}",
  workRealGrowth:
    "Real growth = ({real} − {realBefore}) ÷ {realBefore} = {growth}",
  workInflation:
    "Inflation = ({deflator} − {deflatorBefore}) ÷ {deflatorBefore} = {inflation}",
  workIdentity:
    "Check: (1 + real growth) × (1 + inflation) = {realFactor} × {inflationFactor} = {nominalFactor} = 1 + nominal growth",
  seriesNominal: "Nominal GDP",
  seriesReal: "Real GDP",
  chartTitle: "Nominal and real GDP",
  chartXTitle: "Period",
  chartYTitle: "GDP",
  chartDescription:
    "A line graph of nominal GDP and real GDP over {count} periods. Real GDP is {real} in the last period and nominal GDP is {nominal}.",
  baseNote:
    "Real GDP is measured in the prices of the base year, where the deflator is 100, so in that year real and nominal GDP are equal. Agencies such as the BEA use chained dollars, which change the base more often; the identities above still hold.",
} as const;

export type Strings = typeof REAL_GDP_CALCULATOR_STRINGS;
export type Scale = "units" | "thousand" | "million" | "billion" | "trillion";
type PopulationScale = "people" | "thousand" | "million";

export const SCALE_VALUE: Record<Scale, number> = {
  units: 1,
  thousand: 1e3,
  million: 1e6,
  billion: 1e9,
  trillion: 1e12,
};
const POPULATION_VALUE: Record<PopulationScale, number> = {
  people: 1,
  thousand: 1e3,
  million: 1e6,
};

type RowSets = Record<Known, Row[]>;

const START: RowSets = {
  "nominal-deflator": [
    { id: 1, label: "", first: "20000", second: "100", population: "330" },
    { id: 2, label: "", first: "21630", second: "103", population: "332" },
    { id: 3, label: "", first: "23320", second: "106", population: "334" },
  ],
  "real-deflator": [
    { id: 1, label: "", first: "20000", second: "100", population: "330" },
    { id: 2, label: "", first: "21000", second: "103", population: "332" },
    { id: 3, label: "", first: "22000", second: "106", population: "334" },
  ],
  "nominal-real": [
    { id: 1, label: "", first: "20000", second: "20000", population: "330" },
    { id: 2, label: "", first: "21630", second: "21000", population: "332" },
    { id: 3, label: "", first: "23320", second: "22000", population: "334" },
  ],
};

interface Analysis {
  results: PeriodResult[] | null;
  problem: string | null;
}

function analyse(
  known: Known,
  rows: Row[],
  scale: number,
  populationScale: number,
  names: (index: number) => string,
  invalid: (n: number) => string
): Analysis {
  const periods: PeriodInput[] = [];
  for (const [index, row] of rows.entries()) {
    const first = parseNumberInput(row.first);
    const second = parseNumberInput(row.second);
    if (first === null || second === null || !validPeriod(first, second)) {
      return { results: null, problem: invalid(index + 1) };
    }
    const population = parseNumberInput(row.population);
    periods.push({
      label: row.label.trim() || names(index + 1),
      first,
      second,
      population:
        population !== null && population > 0
          ? population * populationScale
          : null,
    });
  }
  return { results: analysePeriods(known, periods, scale), problem: null };
}

export default function RealGdpCalculatorWidget() {
  const strings = useToolUi(REAL_GDP_CALCULATOR_STRINGS);
  const [known, setKnown] = useState<Known>("nominal-deflator");
  const [currency, setCurrency] = useState("USD");
  const [scale, setScale] = useState<Scale>("billion");
  const [popScale, setPopScale] = useState<PopulationScale>("million");
  const [sets, setSets] = useState<RowSets>(START);
  const [nextId, setNextId] = useState(4);
  const rows = sets[known];
  const setRows = (update: (old: Row[]) => Row[]) =>
    setSets((all) => ({ ...all, [known]: update(all[known]) }));

  const { results, problem } = analyse(
    known,
    rows,
    SCALE_VALUE[scale],
    POPULATION_VALUE[popScale],
    (n) => t(strings.periodDefault, { n: String(n) }),
    (n) => t(strings.invalidPeriod, { n: String(n) })
  );

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.inputsHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="gdp-known"
            label={strings.knownLabel}
            onChange={setKnown}
            options={[
              {
                value: "nominal-deflator",
                label: strings.knownNominalDeflator,
              },
              { value: "real-deflator", label: strings.knownRealDeflator },
              { value: "nominal-real", label: strings.knownNominalReal },
            ]}
            value={known}
          />
          <CurrencyField
            id="gdp-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <SelectField
            id="gdp-scale"
            label={strings.scaleLabel}
            onChange={setScale}
            options={[
              { value: "units", label: strings.scaleUnits },
              { value: "thousand", label: strings.scaleThousand },
              { value: "million", label: strings.scaleMillion },
              { value: "billion", label: strings.scaleBillion },
              { value: "trillion", label: strings.scaleTrillion },
            ]}
            value={scale}
          />
          <SelectField
            id="gdp-popscale"
            label={strings.populationScaleLabel}
            onChange={setPopScale}
            options={[
              { value: "people", label: strings.populationPeople },
              { value: "thousand", label: strings.populationThousand },
              { value: "million", label: strings.populationMillion },
            ]}
            value={popScale}
          />
        </div>
        <GdpForm
          known={known}
          nextId={nextId}
          rows={rows}
          setNextId={setNextId}
          setRows={setRows}
          strings={strings}
        />
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {results && results.length > 0 ? (
          <GdpResults
            currency={currency}
            known={known}
            results={results}
            scale={scale}
            strings={strings}
          />
        ) : null}
        {results ? <Notice>{strings.baseNote}</Notice> : null}
      </section>
    </div>
  );
}
